import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { LS_KEYS } from '@/keys/ls_keys.ts';
import type { User } from '@/models';
import { UserSchema } from '@/models/User.ts';
import { req, withSchema } from '@/req/req.ts';
import { useUserStore } from '@/stores/user.store.ts';

export async function signIn(data: { account: string; password: string }) {
  return await req
    .post<{ data: User }>(
      '/api/user/auth/login',
      {
        password: data.password,
        username: data.account,
      },
      withSchema(z.object({ data: UserSchema })),
    )
    .then((res) => res.data.data);
}

export async function getSignOAuth() {
  const authUri =
    window.location.protocol +
    '//' +
    window.location.hostname +
    '/auth/sign-in';
  const redirect_search = window.location.search;

  const redirect_search_Key = Math.random().toString(36);
  localStorage.setItem(redirect_search_Key, redirect_search);

  return req.get<{ authorization_url: string }>(
    `/api/external-identity/oauth2/microsoft/authorize-url?redirect_uri=${encodeURIComponent(authUri)}&state=${encodeURIComponent(redirect_search_Key)}`,
    withSchema(z.object({ authorization_url: z.string() })),
  );
}

export async function signInWithOAuth({
  code,
  state,
  redirect_uri,
}: {
  code: string;
  state: string;
  redirect_uri: string;
}) {
  return await req
    .post<{ data: User }>(
      '/api/external-identity/oauth2/microsoft/callback',
      {
        code,
        state,
        redirect_uri,
      },
      withSchema(z.object({ data: UserSchema })),
    )
    .then((res) => res.data.data);
}

export async function signOut() {
  return await req
    .post<{ data: User }>('/api/user/auth/logout', undefined)
    .finally(() => {
      localStorage.removeItem(LS_KEYS.USER);
      window.location.replace(
        encodeURI(`/auth/sign-in?redirect=${window.location.pathname}`),
      );
    });
}

export const useSignOutMutation = () => {
  return useMutation({
    mutationFn: signOut,
  });
};

export async function refreshToken(): Promise<User | undefined> {
  try {
    return await req
      .post<{ data: User }>(
        '/api/user/auth/refresh-token',
        {
          refreshToken: useUserStore.getState().user?.refreshToken,
        },
        withSchema(z.object({ data: UserSchema })),
      )
      .then((res) => res.data.data);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_e) {
    signOut();
  }
}

export async function sendVerificationCode(account: string) {
  const res = await req.post(
    `/api/user/auth/send-verify-code?account=${account}`,
  );
  return res.data;
}

export async function signUp(data: {
  email?: string;
  phone_number?: string;
  password: string;
  verify_code: string;
}) {
  const res = await req.post('/api/user/auth/sign-up', data);
  return res.data;
}

export async function resetPassword(data: {
  account: string;
  password: string;
  verify_code: string;
}) {
  const res = await req.post('/api/user/auth/reset-password', data);
  return res.data;
}
